/*
 * Copyright (c) 2023 SHYFTBASE INC.
 * Author: SHYFTBASE INC.
 */
import { getApp, initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { getDatabase } from 'firebase/database'
import { collection, getCountFromServer, getDocs, getFirestore, initializeFirestore, limit, orderBy, query, where } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions'
import { getStorage, ref } from 'firebase/storage'

// Models for each collection
import {
    ActivityLog,
    Calendar,
    Client,
    Collection,
    ContactlessDelivery,
    Contractors,
    DISTRIBUTIONZONES,
    IkeaContractors,
    Invoice,
    Order,
    OrderCount,
    RateConditions,
    ReportSetting,
    TemplateHeader,
    TMSFields,
    Views,
    Zones
} from './Model'

const config = {
    apiKey: process.env.REACT_APP_API_KEY || '',
    authDomain: process.env.REACT_APP_AUTH_DOMAIN || '',
    databaseURL: process.env.REACT_APP_DATABASE_URL || '',
    projectId: process.env.REACT_APP_PROJECT_ID || '',
    appId: process.env.REACT_APP_APP_ID || '',
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET || '',
}

const app = initializeApp(config)

export const fs = initializeFirestore(getApp(), {ignoreUndefinedProperties: true})
export const firestore = initializeFirestore(app, {ignoreUndefinedProperties: true})

export const auth = getAuth()
// auth.languageCode = localStorage ?  localStorage?.getItem('prefLang') || 'en' : 'en'

// Add useAuth hook
export const useAuth = () => {
    return auth
}

export const db = getFirestore(app)

export const rtDb = getDatabase(app)

export const storage = getStorage(getApp())
export const tem_storage = getStorage(getApp(), `${process.env.REACT_APP_TEMP_STORAGE}`)
export const attachments_storage = getStorage(getApp(), `${process.env.REACT_APP_ATTACHMENTS_STORAGE}`)
export const bols_storage= getStorage(getApp(), `${process.env.REACT_APP_BOLS_STORAGE}`)
export const templates_storage = getStorage(getApp(), `${process.env.REACT_APP_TEMPLATES_STORAGE}`)
export const gsReference_template = ref(storage, `${process.env.REACT_APP_FSC_TEMPLATE}`);
export const assetImage_storage = getStorage(getApp(), `tms_assets_images`)

export const getStorageBucketRef = (bucket: string) => {
    console.log(`🔍 [firebaseConfig] getStorageBucketRef called with bucket: "${bucket}"`)
    
    let storageRef: any
    switch(bucket) {
        case 'attachments':
            storageRef = attachments_storage
            break
        case 'bols':
            storageRef = bols_storage
            break
        case 'template':
            storageRef = templates_storage
            break
        case 'temporary':
            storageRef = tem_storage
            break
        case "tms_assets_images":
            storageRef = assetImage_storage
            break
        case 'ai-shipment-intakes':
            storageRef = storage // Uses default storage bucket, folders created automatically
            break
        default:
            storageRef = storage
            break
    }
    
    return storageRef
}

export const fb = getApp()
export const functions = getFunctions(getApp())

// Connect to Functions emulator
// if (process.env.NODE_ENV === 'development') {
//     connectFunctionsEmulator(functions, 'localhost', 5001);
// }

export const getProjectCollection = (name: string) => {
    return collection(firestore, name)
}

export {
    collection, getDocs, limit, orderBy, query, where
}
/* END OF CLEAN VERSION */

export const activityLogCollection = collection(fs, 'ACTIVITY_LOGS_MAIN_PORTAL')

export const activityLog = ActivityLog(activityLogCollection)

export const validateEmail = httpsCallable(functions, 'validateEmail')
export const getAvailabilities = httpsCallable(functions, 'getAvailabilities')

export const createOrder = httpsCallable(functions, 'createOrder')
export const updateOrder = httpsCallable(functions, 'updateOrder')
export const cancelOrder = httpsCallable(functions, 'cancelOrder')
export const requestInfo = httpsCallable(functions, 'requestInfo')
export const approveOrder = httpsCallable(functions, 'approveOrder')
export const fetchHistory = httpsCallable(functions, 'fetchHistory')
export const callOpenRouter = httpsCallable(functions, 'OPENROUTER-callOpenRouter')
export const rescheduleOrderDelivery = httpsCallable(functions, 'CUSTOMER-rescheduleOrderDelivery')
export const handlePendingOrder = httpsCallable(functions, 'STORE-handlePendingOrder')
export const saveTemplateHeader = httpsCallable(functions, 'SAVE_TEMPLATE_HEADER-handler')
export const getQuote = httpsCallable(functions, 'BILLING-getQuote')

/* MAIN PORTAL COLLECTION */
export const activitiesCollectionInstance = collection(fs, 'ACTIVITY_LOGS')

/* TBD: We need to streamline the data Model. We have lots of duplicate/redundant code block in each Model */
export const ordersCollectionInstance = collection(fs, 'TMS_ORDERS')
export const ordersCollection = Order(ordersCollectionInstance)

export const inventoryCollectionInstance = collection(fs, 'TMS_INVENTORY')
export const inventoryCollection = Order(inventoryCollectionInstance)  

export const assetsCollectionInstance = collection(fs, 'TMS_ASSETS')
export const assetsCollection = Order(assetsCollectionInstance)

export const tmsLinkRefsCollectionInstance = collection(fs, 'TMS_LINK_REFS')

export const rateTableTMSCollectionInstance = collection(firestore, 'RATE_TABLES_LIVE_V2')
export const rateTableTMSCollection = Order(rateTableTMSCollectionInstance)

export const templateHeaderCollectionInstance = collection(fs, 'IMPORT_TEMPLATES')
export const templateHeaderCollection = TemplateHeader(templateHeaderCollectionInstance)

export const ordersCountCollectionInstance = collection(fs, 'ORDERS_COUNT')
export const ordersCountCollection = OrderCount(ordersCountCollectionInstance)

export const calendarCollectionInstance = collection(fs, 'CALENDAR')
export const calendarCollection = Calendar(calendarCollectionInstance)

export const invoiceCollectionInstance = collection(fs, 'INVOICE_MANAGEMENT')
export const invoiceCollection = Invoice(invoiceCollectionInstance)

export const rateConditionsLiveCollection = collection(firestore, 'RATE_TABLES_CONDITIONS')
export const rateConditionsCollection = RateConditions(rateConditionsLiveCollection)

export const rateServicesLiveCollection = collection(firestore, 'RATE_TABLES_SERVICES')
export const rateServicesCollection = RateConditions(rateServicesLiveCollection)

export const rateDedicatedServicesLiveCollection = collection(firestore, 'GLOBALDEF_DEDICATED_SERVICES')
export const rateDedicatedServicesCollection = RateConditions(rateDedicatedServicesLiveCollection)

export const dedicatedStemsLiveCollection = collection(firestore, 'DEDICATED_STEMS')
export const dedicatedStemsCollection = RateConditions(dedicatedStemsLiveCollection)

export const optionTypesLiveCollection = collection(firestore, 'GLOBALDEF_ORDERTYPES')
export const optionTypesCollection = RateConditions(optionTypesLiveCollection)

export const substatsLiveCollection = collection(firestore, 'GLOBALDEF_SUBSTATUSES')
export const substatsCollection = RateConditions(substatsLiveCollection)

export const ratezonesLiveCollection = collection(firestore, 'RATE_TABLES_ZONES')
export const ratezonesCollection = RateConditions(ratezonesLiveCollection)

export const regionsDefLiveCollection = collection(firestore, 'REGIONS')
export const regionsDefCollection = Order(regionsDefLiveCollection)

export const fscratesLiveCollection = collection(firestore, 'RATE_TABLES_FSC_RATES')
export const fscratesCollection = RateConditions(fscratesLiveCollection)

export const dedicatedTiersLiveCollection = collection(firestore, 'RATE_TABLES_DEDICATED_TIERS')
export const dedicatedTiersCollection = RateConditions(dedicatedTiersLiveCollection)

export const escrowTypesLiveCollection = collection(firestore, 'RATE_TABLES_ESCROW_TYPES')
export const escrowTypesCollection = RateConditions(escrowTypesLiveCollection)

export const clientsCollectionInstance = collection(firestore, 'CLIENTS')
export const clientsCollection = Client(clientsCollectionInstance)

export const allContractorsCollectionInstance = collection(firestore, `${process.env.REACT_APP_COLLECTION_CONTRACTORS}`)
export const allContractorsCollection = Contractors(allContractorsCollectionInstance)

export const distributionZonesCollectionInstance = collection(firestore, `${process.env.REACT_APP_COLLECTION_DISTRIBUTIONZONES}`)
export const distributionZonesCollection = DISTRIBUTIONZONES(distributionZonesCollectionInstance)

export const reportsSettingCollectionInstance = collection(firestore, 'REPORTS_SETTING')
export const reportsSettingCollection = ReportSetting(reportsSettingCollectionInstance)

export const contactlessDeliveryCollectionInstance = collection(firestore, 'CONTACTLESS_DELIVERY_SETTING')
export const contactlessDeliveryCollection = ContactlessDelivery(contactlessDeliveryCollectionInstance)

export const ikeaContractorsCollectionInstance = collection(firestore, 'IKEA_CONTRACTORS')
export const ikeaContractors = IkeaContractors(ikeaContractorsCollectionInstance)

export const ikeaPostalCodeCollectionInstance = collection(firestore, 'IKEA_POSTALCODE_ZONES')
export const ikeaPostalCode = IkeaContractors(ikeaPostalCodeCollectionInstance)

export const tmsConfigurationFieldsCollectionInstance = collection(firestore, 'CONFIGURATIONS/TABLE/FIELDS')
export const tmsConfigurationFieldsCollection = TMSFields(tmsConfigurationFieldsCollectionInstance)

export const inventoryFieldsCollectionInstance = collection(firestore, 'CONFIGURATIONS/TABLE/INVENTORYFIELDS')
export const inventoryFieldsCollection = TMSFields(inventoryFieldsCollectionInstance)

export const midMileFieldsCollectionInstance = collection(firestore, 'CONFIGURATIONS/TABLE/MIDMILEFIELDS')
export const midMileFieldsCollection = TMSFields(midMileFieldsCollectionInstance)

export const globalUnitsCollectionInstance = collection(firestore, 'CONFIGURATIONS/OPTIONS/GlobalUnits')
export const globalUnitsCollection = Collection(globalUnitsCollectionInstance)

export const OrdersCollectionInstance = collection(firestore, 'TMS_ORDERS')
export const OrdersCollection = Order(OrdersCollectionInstance)

export const BolsCollectionInstance = collection(firestore, 'BOLS')
export const BolsCollection = Order(BolsCollectionInstance)

export const tableViews = collection(firestore, 'TMS_VIEWS')
export const tableViewsCollection = Collection(tableViews)

export const vehiclesViews = collection(firestore, 'VEHICLES')
export const vehiclesCollection = Collection(vehiclesViews)

export const actions = collection(firestore, 'ACTIONS_NEW')
export const actionsCollection = Collection(actions)

export const roles = collection(firestore, 'ROLES_NEW')
export const rolesCollection = Collection(roles)

export const users = collection(firestore, `${process.env.REACT_APP_COLLECTION_USERS}`)
export const usersCollection = Collection(users)

export const fleet = collection(firestore, 'FLEET')
export const fleetCollection = Collection(fleet)

export const vendors = collection(firestore, 'VENDORS')
export const vendorsCollection = Collection(vendors)

export const contractors = collection(firestore, `CONTRACTORS`)
export const contractorsCollection = Collection(contractors)

// Transport Partners Collection (unified partner management)
export const transportPartners = collection(firestore, 'TRANSPORT_PARTNERS')
export const transportPartnersCollection = Collection(transportPartners)

export const notifications = collection(firestore, "NOTIFICATIONS")
export const notificationsCollection = Collection(notifications)

export const notificationLogs = collection(firestore, "NOTIFICATION_LOGS")
export const notificationLogsCollection = Collection(notificationLogs)

export const delayedNotificationSettings = collection(firestore, "DELAYED_NOTIFICATION_SETTINGS")
export const delayedNotificationSettingsCollection = Collection(delayedNotificationSettings)

export const delayNotificationTracking = collection(firestore, "DELAY_NOTIFICATION_TRACKING")
export const delayNotificationTrackingCollection = Collection(delayNotificationTracking)

export const configurations = collection(firestore, "CONFIGURATIONS")
export const configurationsCollection = Collection(configurations)

export const issues = collection(firestore, "ISSUES")
export const issuesCollection = Collection(issues)

export const addressBook = collection(firestore, 'ADDRESS_BOOK')
export const addressBookCollection = Collection(addressBook)

export const getOrdersCollection = (name: string) => {
    const orders = collection(firestore, name)
    return Order(orders)
}

export const getCollection = (name: string) => {
    const collectionRef = collection(firestore, name)
    return Collection(collectionRef)
}

export const getOrderSubCollection = (name: string, id: string, subName: string) => {
    const ref = collection(firestore, `${name}/${id}/${subName}`)
    return Collection(ref)
}

export const getOrderDocument = (id: string) => {
    return ordersCollection.doc(id)
}

export const getDistributionZoneCollection = (name: string) => {
    const distributionzone = collection(firestore, name)
    return Zones(distributionzone)
}

export const getContractorsCollection = (name: string) => {
    const distributionzone = collection(firestore, name)
    return Zones(distributionzone)
}

export const getViewsCollection = (name: string) => {
    const views = collection(firestore, name)
    return Views(views)
}

// Mid-Mile Collections
export const midMileShipments = collection(firestore, 'MIDMILE_SHIPMENTS')
export const midMileShipmentsCollection = Collection(midMileShipments)

// Legacy reference for backward compatibility - use existing contractors collection
export const midMileCarriers = contractors
export const midMileCarriersCollection = contractorsCollection

export const midMileRateTables = collection(firestore, 'MIDMILE_RATE_TABLES')
export const midMileRateTablesCollection = Collection(midMileRateTables)

export const midMileTariffs = collection(firestore, 'MIDMILE_TARIFFS')
export const midMileTariffsCollection = Collection(midMileTariffs)

export const midMileAuctions = collection(firestore, 'MIDMILE_AUCTIONS')
export const midMileAuctionsCollection = Collection(midMileAuctions)

export const midMileBids = collection(firestore, 'MIDMILE_BIDS')
export const midMileBidsCollection = Collection(midMileBids)

// Use existing DISTRIBUTIONZONES collection for mid-mile hubs
export const midMileHubs = collection(firestore, 'DISTRIBUTIONZONES')
export const midMileHubsCollection = DISTRIBUTIONZONES(midMileHubs)

// Use existing LANES collection for mid-mile transit lanes
export const lanes = collection(firestore, 'LANES')
export const lanesCollection = Collection(lanes)

export const getCountForCollection = async (collection: any) => {

    const snapshot = await getCountFromServer(collection)
    return snapshot.data().count;
}

export const getCountForQuery = async (queryRef: any) => {
        const snapshot = await getCountFromServer(queryRef)
        return Promise.resolve(snapshot?.data()?.count || 0);
    }

/* DO WE NEED ANONYMOUS EXPORTS IN ADDITION TO THE NAMED EXPORTS ABOVE? */
export default {
    allContractorsCollection,
    auth,
    clientsCollection,
    contactlessDeliveryCollection,
    distributionZonesCollection,
    ikeaContractors,
    invoiceCollection,
    ordersCollection,
    ordersCountCollection,
    reportsSettingCollection,
    storage,
    templateHeaderCollection,
    gsReference_template
}
